// Overlay metadata: device image, LCD bounds, file list.
// Generated by `python tools/build-assets.py` into public/assets/overlay-meta.json.

export interface LcdBounds {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

export interface OverlayMeta {
  readonly base: string;
  readonly lcd_window_overlay?: string | null;
  readonly lcd_window_overlay_hole_bounds?: LcdBounds | null;
  readonly lcd_window_overlay_bounds?: LcdBounds | null;
  readonly image_size: readonly [number, number];
  readonly lcd_window_bounds: LcdBounds;
  readonly lcd_active_bounds: LcdBounds;
  /** Backwards-compatible alias for older generated metadata. */
  readonly lcd_bounds: LcdBounds;
  readonly lcd_window_pixel_bounds?: readonly [number, number, number, number];
  readonly lcd_pixel_bounds: readonly [number, number, number, number];
  readonly lcd_source: string;
  readonly files: ReadonlyArray<{
    readonly name: string;
    readonly src_bytes: number;
    readonly out_bytes: number;
    readonly ratio: number;
  }>;
}

let cached: OverlayMeta | undefined;

export const LCD_ACTIVE_SCALE = 1.1;

interface RawOverlayMeta extends Omit<OverlayMeta, 'lcd_window_bounds' | 'lcd_active_bounds'> {
  readonly lcd_window_bounds?: LcdBounds;
  readonly lcd_active_bounds?: LcdBounds;
}

export async function loadOverlayMeta(): Promise<OverlayMeta> {
  if (cached) return cached;
  const r = await fetch('/assets/overlay-meta.json');
  if (!r.ok) throw new Error(`overlay-meta.json: ${r.status}`);
  const raw = (await r.json()) as RawOverlayMeta;
  cached = {
    ...raw,
    lcd_window_bounds: raw.lcd_window_bounds ?? raw.lcd_bounds,
    lcd_active_bounds: raw.lcd_active_bounds ?? raw.lcd_bounds,
  };
  return cached;
}

/** Set LCD canvas position/size from overlay metadata (as % of device frame). */
export function applyLcdBounds(canvas: HTMLCanvasElement, bounds: LcdBounds): void {
  applyBounds(canvas, bounds);
}

/** Scale an overlay-aligned rectangle from its current center point. */
export function scaleBoundsFromCenter(bounds: LcdBounds, scale: number): LcdBounds {
  const width = bounds.width * scale;
  const height = bounds.height * scale;
  const centerX = bounds.left + bounds.width / 2;
  const centerY = bounds.top + bounds.height / 2;
  return {
    top: centerY - height / 2,
    left: centerX - width / 2,
    width,
    height,
  };
}

/** Position a full artwork image so an inner source rect aligns to a target rect. */
export function boundsFromInnerAlignment(target: LcdBounds, sourceInner: LcdBounds): LcdBounds {
  if (sourceInner.width <= 0 || sourceInner.height <= 0) return target;
  const width = target.width / sourceInner.width;
  const height = target.height / sourceInner.height;
  return {
    top: target.top - sourceInner.top * height,
    left: target.left - sourceInner.left * width,
    width,
    height,
  };
}

/** Set any overlay-aligned element bounds from metadata. */
export function applyBounds(el: HTMLElement, bounds: LcdBounds): void {
  el.style.top = pct(bounds.top);
  el.style.left = pct(bounds.left);
  el.style.width = pct(bounds.width);
  el.style.height = pct(bounds.height);
}

function pct(v: number): string {
  return `${(v * 100).toFixed(3)}%`;
}
